import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Scrollbars from "react-custom-scrollbars-2";
import { SidebarData } from "./sidebarData";
import { useDispatch, useSelector } from "react-redux";
import {
  setExpandMenu,
  setMobileSidebar,
} from "../../../core/data/redux/commonSlice";
import RouteSearch from "../../../formHelper/RouteSearch";
import type { RootState, AppDispatch } from "../../../core/data/redux/store";

/* ================= TYPES ================= */

interface Profile {
  profile_image?: File | string | null;
  name?: string;
  email?: string;
}

interface Permission {
  id: number;
  role_id: number;
  name: string;
  role: {
    permissions: Record<string, Record<string, string>>;
  };
}

/* ================= COMPONENT ================= */

const Sidebar: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const isActive = (link?: string) => link && location.pathname === link;
  const profile = useSelector(
    (state: RootState) => state.profile?.data
  ) as Profile | null;

  const permission = useSelector(
    (state: RootState) => state.rolesPermissions?.data
  ) as Permission | null;

  const [subOpen, setSubopen] = useState<string>("");
  const [subsidebar, setSubsidebar] = useState<string>("");

  /* ================= PERMISSION CORE ================= */

  const permissionMap = useMemo(() => {
    return permission?.role?.permissions ?? {};
  }, [permission]);

  // check single module permission (view OR edit)
  const hasModulePermission = (module?: any): boolean => {
    if (module == "expances") {
      module = "expanses";
    }
    if (!module) return false;

    const viewKey = `view_${module}`;
    const editKey = `edit_${module}`;

    return Object.values(permissionMap).some((perms: any) =>
      Boolean(perms?.[viewKey] || perms?.[editKey])
    );
  };

  /**
   * FINAL resolver:
   * Works for any menu item, any depth
   */
  const canRenderMenu = (item: any): boolean => {
    if (!item) return false;

    // 1️⃣ Explicit list of codes (parent/group menus)
    if (Array.isArray(item.subMenuCodeList) && item.subMenuCodeList.length) {
      return item.subMenuCodeList.some((code: string) =>
        hasModulePermission(code)
      );
    }

    // 2️⃣ Direct code (leaf menus)
    if (item.code) {
      return hasModulePermission(item.code);
    }

    // 3️⃣ Children (fallback / recursive)
    if (Array.isArray(item.submenuItems) && item.submenuItems.length) {
      return item.submenuItems.some(canRenderMenu);
    }

    // 4️⃣ Default
    return false;
  };

  /* ================= SIDEBAR STATE ================= */

  const toggleSidebar = (title: string) => {
    localStorage.setItem("menuOpened", title);
    setSubopen((prev) => (prev === title ? "" : title));
  };

  const toggleSubsidebar = (subitem: string) => {
    setSubsidebar((prev) => (prev === subitem ? "" : subitem));
  };

  const handleMenuClick = () => {
    if (window.innerWidth <= 1024) {
      dispatch(setMobileSidebar(false));
    }
  };

  /* ================= PROFILE IMAGE ================= */

  let previewSrc: string | undefined;
  if (profile?.profile_image) {
    previewSrc =
      profile.profile_image instanceof File
        ? URL.createObjectURL(profile.profile_image)
        : String(profile.profile_image);
  }

  /* ================= EFFECTS ================= */

  useEffect(() => {
    setSubopen(String(localStorage.getItem("menuOpened") || ""));
  }, []);

  useEffect(() => {
    setSubopen(String(localStorage.getItem("menuOpened") || ""));
  }, [location.pathname]);

  /* ================= RENDER ================= */

  return (
    <div className="sidebar" id="sidebar">
      <Scrollbars>
        <div className="sidebar-inner slimscroll">
          <div id="sidebar-menu" className="sidebar-menu">
            <ul>
              <li className="clinicdropdown theme">
                <Link to="/profile-details" onClick={handleMenuClick}>
                  <img
                    src={previewSrc || "/assets/img/profile.png"}
                    className="img-fluid"
                    alt="Profile"
                  />
                  <div className="user-names">
                    <h5>{profile?.name || ""}</h5>
                    <h6>{profile?.email || ""}</h6>
                  </div>
                </Link>
              </li>
              <li>
                <RouteSearch />
              </li>
            </ul>

            <ul>
              {SidebarData.filter(canRenderMenu).map((mainLabel, index) => (
                <li className="clinicdropdown" key={index}>
                  <ul>
                    {mainLabel.submenuItems
                      ?.filter(canRenderMenu)
                      .map((title) => {
                        const isTitleActive =
                          isActive(title?.link) ||
                          title?.submenuItems?.some(
                            (item) =>
                              isActive(item?.link) ||
                              item?.submenuItems?.some((leaf) =>
                                isActive(leaf?.link)
                              )
                          );

                        const onTopLevelClick = (e: React.MouseEvent) => {
                          if (title?.submenu) {
                            e.preventDefault();
                            toggleSidebar(title.label!);
                          } else {
                            handleMenuClick();
                          }
                        };

                        return (
                          <li
                            key={title.label}
                            className={`submenu ${
                              isTitleActive ? "active" : ""
                            }`}
                          >
                            <Link
                              to={title?.submenu ? "#" : title?.link || "#"}
                              onClick={onTopLevelClick}
                              className={`${
                                subOpen === title.label ? "subdrop" : ""
                              } ${isTitleActive ? "active" : ""}`}
                            >
                              {title.icon && <i className={title.icon} />}
                              <span>{title.label}</span>
                              <span
                                className={title.submenu ? "menu-arrow" : ""}
                              />
                            </Link>

                            <ul
                              style={{
                                display:
                                  subOpen === title.label || isTitleActive
                                    ? "block"
                                    : "none",
                              }}
                            >
                              {title.submenuItems
                                ?.filter(canRenderMenu)
                                .map((item) => {
                                  const isItemActive =
                                    isActive(item?.link) ||
                                    item?.submenuItems?.some((leaf) =>
                                      isActive(leaf?.link)
                                    );

                                  return (
                                    <li
                                      key={item.label}
                                      className={`submenu submenu-two ${
                                        isItemActive ? "active" : ""
                                      }`}
                                    >
                                      <Link
                                        to={item?.link || "#"}
                                        onClick={() =>
                                          item.submenu
                                            ? toggleSubsidebar(item.label!)
                                            : handleMenuClick()
                                        }
                                        className={
                                          isItemActive ? "active subdrop" : ""
                                        }
                                      >
                                        {item.label}
                                      </Link>

                                      <ul
                                        style={{
                                          display:
                                            subsidebar === item.label ||
                                            isItemActive
                                              ? "block"
                                              : "none",
                                        }}
                                      >
                                        {item.submenuItems
                                          ?.filter(canRenderMenu)
                                          .map((leaf) => (
                                            <li
                                              key={leaf.label}
                                              className={
                                                isActive(leaf.link)
                                                  ? "active"
                                                  : ""
                                              }
                                            >
                                              <Link
                                                to={leaf.link!}
                                                onClick={handleMenuClick}
                                                className={
                                                  isActive(leaf.link)
                                                    ? "active"
                                                    : ""
                                                }
                                              >
                                                {leaf.label}
                                              </Link>
                                            </li>
                                          ))}
                                      </ul>
                                    </li>
                                  );
                                })}
                            </ul>
                          </li>
                        );
                      })}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Scrollbars>
    </div>
  );
};

export default Sidebar;
